import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setFavicon } from '@/store/slices/project';
import { selectFavicon } from '@/store/slices/settings/selectors';
import Text from '@ui/text/text/text';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { TooltipPortalPosition } from '@ui/tooltip-portal/tooltip-portal-types';
import UploadLarge from '@ui/uploads/upload-large';
import OptionalComponent from '@ui/optional-component/optional-component';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import { isObject } from 'underscore';
import Container from '@ui/container/container';
import btvFavicon from '@images/btv-favicon.png';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import styles from './settings-edit-panel.module.css';
import classNames from 'classnames';

const FaviconUploader = () => {
  const dispatch = useAppDispatch();
  const favicon = useAppSelector(selectFavicon);
  const { projectUuid } = useParams<{ projectUuid: string; language: string }>();

  const [uploading, setUpLoading] = useState(false);

  const handleFile = useCallback(
    async (file: File | FileList) => {
      if (!(file instanceof File)) {
        return;
      }

      setUpLoading(true);
      try {
        const endpoint = await GetUploadEndpoint({ filename: getUploadFilename(file.name) });

        if (isObject(endpoint) && endpoint.url) {
          const uploadedFavicon = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );

          SetItemContentType({ filename: uploadedFavicon, contentType: file.type }).catch(console.error);

          if (projectUuid && uploadedFavicon) {
            dispatch(setFavicon({ project_uuid: projectUuid, favicon: uploadedFavicon }));
          }
        }
      } catch (error) {
        console.error('Error during file upload:', error);
        handleImageUploadErrors(error);
      } finally {
        setUpLoading(false);
      }
    },
    [dispatch, projectUuid]
  );

  return (
    <Container className={classNames('flex jc-center position-relative', styles.faviconUploader)}>
      <TooltipPortal
        tooltipContent={
          <Text className="P3" color="var(--gray-true-dark-200)">
            32 x 32px png recommended
          </Text>
        }
        tooltipWrapperClass="border-radius-8 padding-2"
        childrenWrapperClass="width-64 height-64"
        position={TooltipPortalPosition.BOTTOM}
        delayStart={500}
      >
        <UploadLarge
          displayImageUrl={favicon}
          maxSizeInBytes={20 * 1024}
          onFile={handleFile}
          uploading={uploading}
          allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '0px',
            background: 'transparent',
            border: '1px dashed var(--gray-600)',
            backgroundRepeat: 'no-repeat',
            // this is intended to look bad if the image is not square. Crawlers will display the
            // image stretched if it is not square so we want to impress upon the user that the image
            // that was uploaded is going to appear distorted
            backgroundSize: '100% 100%', // <--- DO NOT CHANGE THIS PROP PER BDEV-4814
          }}
          placeholder="Favicon"
          CustomTitle={
            <OptionalComponent display={!favicon && !uploading}>
              <div className="flex-column ai-center gap-10">
                <img src={btvFavicon} height={64} width={64} />
              </div>
            </OptionalComponent>
          }
        />
      </TooltipPortal>
      <div className={styles.faviconHover}>
        <Icon color="var(--gray-true-dark-0)" name={ICONS.EDIT_SOLID} size={12} />
      </div>
    </Container>
  );
};

export default FaviconUploader;
